import {
    Box,
    Button,
    Center,
    Flex,
    Input,
    List,
    ListItem,
    Spinner,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tabs,
    Tab,
    TabList,
    TabPanels,
    TabPanel,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
} from "@chakra-ui/react";
import {
    useLazySearchFlightsQuery,
    useSelectOfferMutation,
} from "../../../orderApi/amadeusApi";
import { setSelectedOffer } from "../../../store/travelSlice/amadeusFlightSlice";
import { useAppDispatch } from "../../../store/hooks/hooks";
import { useState } from "react";
import airportList from "../../../assets/african_airports.json";
import { useNavigate } from "react-router-dom";

const cardsBorderRadii ="8px";
type Airport = {
    name: string;
    code: string;
};

export const FlightSearch = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [tripType, setTripType] = useState<"oneway" | "roundtrip">("oneway");

    const [params, setParams] = useState({
        originLocationCode: "JNB",
        destinationLocationCode: "CPT",
        departureDate: "2025-06-20",
        returnDate: "2025-06-27",
        adults: "1",
        children: "0",
        travelClass: "ECONOMY",
        nonStop: "false",
        max: "10",
    });

    const [triggerSearch, { data: departureData, isFetching, isError, error }] =
        useLazySearchFlightsQuery();
    const [triggerReturnSearch, { data: returnData }] =
        useLazySearchFlightsQuery();

    const [results, setResults] = useState<Airport[]>([]);
    const [activeField, setActiveField] = useState<"origin" | "destination" | null>(null);

    const [selectedDepartureId, setSelectedDepartureId] = useState<string | undefined>();
    const [selectedReturnId, setSelectedReturnId] = useState<string | undefined>();
    const [selectedDepartureFlight, setSelectedDepartureFlight] = useState<any>(null);
    const [selectedReturnFlight, setSelectedReturnFlight] = useState<any>(null);

    const handleSearch = () => {
        triggerSearch({
            ...params,
        });

        if (tripType === "roundtrip") {
            triggerReturnSearch({
                ...params,
                originLocationCode: params.destinationLocationCode,
                destinationLocationCode: params.originLocationCode,
                departureDate: params.returnDate,
            });
        }

        setSelectedDepartureId(undefined);
        setSelectedReturnId(undefined);
        setSelectedDepartureFlight(null);
        setSelectedReturnFlight(null);
    };

    const handleAirportInput = (text: string, field: "origin" | "destination") => {
        setParams((prev) => ({
            ...prev,
            [`${field}LocationCode`]: text,
        }));

        if (text.length < 2) {
            setResults([]);
            setActiveField(null);
            return;
        }

        const lower = text.toLowerCase();
        const filtered = airportList.filter(
            (airport) =>
                airport.name.toLowerCase().includes(lower) ||
                airport.code.toLowerCase().includes(lower)
        );

        setResults(filtered.slice(0, 10));
        setActiveField(field);
    };

    const flightOffers = departureData?.data || [];
    const returnOffers = returnData?.data || [];

    const totalPassengers =
        parseInt(params.adults) + parseInt(params.children || "0");

    const totalFare = (
        (parseFloat(selectedDepartureFlight?.price?.total || "0") +
            parseFloat(selectedReturnFlight?.price?.total || "0")) *
        totalPassengers
    ).toFixed(2);

    const handleBookNow = () => {
        if (tripType === "oneway" && selectedDepartureFlight) {
            sessionStorage.setItem("selectedFlight", JSON.stringify({
                ...selectedDepartureFlight,
                totalPassengers,
            }));
            // dispatch(setSelectedOffer(selectedDepartureFlight));
            navigate(`/flight-booking/${selectedDepartureFlight.id}`);
        }

        if (
            tripType === "roundtrip" &&
            selectedDepartureFlight &&
            selectedReturnFlight
        ) {
            const combinedTrip = {
                departure: selectedDepartureFlight,
                return: selectedReturnFlight,
                total: totalFare,
                totalPassengers,
            };

            sessionStorage.setItem("selectedFlightRoundTrip", JSON.stringify(combinedTrip));
            //dispatch(setSelectedOffer(combinedTrip));
            navigate(
                `/flight-booking/${selectedDepartureFlight.id}_${selectedReturnFlight.id}`
            );
        }
    };

    return (
        <Box p={4}>
            <Tabs onChange={(i) => setTripType(i === 0 ? "oneway" : "roundtrip")}>
                <TabList mb={4}>
                    <Tab>One Way</Tab>
                    <Tab>Round Trip</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel p={0} />
                    <TabPanel p={0} />
                </TabPanels>
            </Tabs>

            {/* Airports */}
            <Box position="relative" mb={4}>
                <Input
                    placeholder="Origin"
                    value={params.originLocationCode}
                    onChange={(e) => handleAirportInput(e.target.value, "origin")}
                />
                {activeField === "origin" && results.length > 0 && (
                    <List position="absolute" bg="white" zIndex={10} w="100%" maxH="200px" overflowY="auto">
                        {results.map((airport) => (
                            <ListItem
                                key={airport.code}
                                p={2}
                                _hover={{ bg: "gray.100", cursor: "pointer" }}
                                onClick={() => {
                                    setParams((prev) => ({ ...prev, originLocationCode: airport.code }));
                                    setResults([]);
                                    setActiveField(null);
                                }}
                            >
                                {airport.name} ({airport.code})
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            <Box position="relative" mb={4}>
                <Input
                    placeholder="Destination"
                    value={params.destinationLocationCode}
                    onChange={(e) => handleAirportInput(e.target.value, "destination")}
                />
                {activeField === "destination" && results.length > 0 && (
                    <List position="absolute" bg="white" zIndex={10} w="100%" maxH="200px" overflowY="auto">
                        {results.map((airport) => (
                            <ListItem
                                key={airport.code}
                                p={2}
                                _hover={{ bg: "gray.100", cursor: "pointer" }}
                                onClick={() => {
                                    setParams((prev) => ({ ...prev, destinationLocationCode: airport.code }));
                                    setResults([]);
                                    setActiveField(null);
                                }}
                            >
                                {airport.name} ({airport.code})
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            {/* Date and Search */}
            <Stack direction="row" spacing={4} mb={4}>
                <Input
                    type="date"
                    value={params.departureDate}
                    onChange={(e) =>
                        setParams((prev) => ({ ...prev, departureDate: e.target.value }))
                    }
                />
                {tripType === "roundtrip" && (
                    <Input
                        type="date"
                        value={params.returnDate}
                        onChange={(e) =>
                            setParams((prev) => ({ ...prev, returnDate: e.target.value }))
                        }
                    />
                )}
            </Stack>

            {/* Passengers and class */}
            <Stack direction="row" spacing={6} mb={4}>
                <FormControl>
                    <FormLabel>Adults</FormLabel>
                    <Input
                        type="number"
                        min="1"
                        value={params.adults}
                        onChange={(e) =>
                            setParams((prev) => ({ ...prev, adults: e.target.value }))
                        }
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Children</FormLabel>
                    <Input
                        type="number"
                        min="0"
                        value={params.children}
                        onChange={(e) =>
                            setParams((prev) => ({ ...prev, children: e.target.value }))
                        }
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Cabin</FormLabel>
                    <RadioGroup
                        value={params.travelClass}
                        onChange={(val) =>
                            setParams((prev) => ({ ...prev, travelClass: val }))
                        }
                    >
                        <Stack direction="row">
                            <Radio value="ECONOMY">Economy</Radio>
                            <Radio value="BUSINESS">Business</Radio>
                        </Stack>
                    </RadioGroup>
                </FormControl>

                <Button onClick={handleSearch} isLoading={isFetching} mt={6}>
                    Search
                </Button>
            </Stack>

            {isFetching && (
                <Center mt={4}>
                    <Spinner size="lg" />
                    <Text ml={2}>Searching for flights...</Text>
                </Center>
            )}

            {isError && (
                <Text color="red.500" mt={4}>
                    ❌ Error fetching flights: {(error as any)?.message || "Unknown error"}
                </Text>
            )}

            <Flex gap="40px">
                {/* Departure Flights */}
                {flightOffers.length > 0 && (
                    <Box>
                        <Text fontWeight="bold" mb={2}>Departure Flights</Text>
                        <RadioGroup value={selectedDepartureId} onChange={(id) => {
                            setSelectedDepartureId(id);
                            const flight = flightOffers.find((f: any) => f.id === id);
                            setSelectedDepartureFlight(flight);
                        }}>
                            <Table variant="simple" mb={6}>
                                <Thead>
                                    <Tr>
                                        <Th>Select</Th>
                                        <Th>Airline</Th>
                                        <Th>From</Th>
                                        <Th>To</Th>
                                        <Th>Price</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {flightOffers.map((offer: any) => {
                                        const first = offer.itineraries?.[0]?.segments?.[0];
                                        const last = offer.itineraries?.[0]?.segments?.at(-1);
                                        return (
                                            <Tr key={offer.id}>
                                                <Td><Radio value={offer.id} /></Td>
                                                <Td>{offer.validatingAirlineCodes?.join(", ")}</Td>
                                                <Td>{first?.departure?.iataCode}</Td>
                                                <Td>{last?.arrival?.iataCode}</Td>
                                                <Td>{offer.price?.total} {offer.price?.currency}</Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </RadioGroup>
                    </Box>
                )}

                {/* Return Flights */}
                {tripType === "roundtrip" && returnOffers.length > 0 && (
                    <Box>
                        <Text fontWeight="bold" mb={2}>Return Flights</Text>
                        <RadioGroup value={selectedReturnId} onChange={(id) => {
                            setSelectedReturnId(id);
                            const flight = returnOffers.find((f: any) => f.id === id);
                            setSelectedReturnFlight(flight);
                        }}>
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Select</Th>
                                        <Th>Airline</Th>
                                        <Th>From</Th>
                                        <Th>To</Th>
                                        <Th>Price</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {returnOffers.map((offer: any) => {
                                        const first = offer.itineraries?.[0]?.segments?.[0];
                                        const last = offer.itineraries?.[0]?.segments?.at(-1);
                                        return (
                                            <Tr key={offer.id}>
                                                <Td><Radio value={offer.id} /></Td>
                                                <Td>{offer.validatingAirlineCodes?.join(", ")}</Td>
                                                <Td>{first?.departure?.iataCode}</Td>
                                                <Td>{last?.arrival?.iataCode}</Td>
                                                <Td>{offer.price?.total} {offer.price?.currency}</Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </RadioGroup>
                    </Box>
                )}

                {/* Trip Summary */}
                {(tripType === "oneway" && selectedDepartureFlight) ||
                    (tripType === "roundtrip" && selectedDepartureFlight && selectedReturnFlight) ? (
                    <Box mt={6} p={4} borderWidth="1px" borderRadius={cardsBorderRadii}>
                        <Text fontSize="lg" mb={2} fontWeight="bold">Trip Summary</Text>
                        <Text mb={2}>
                            ✈️ Departure: {selectedDepartureFlight.itineraries?.[0]?.segments?.[0]?.departure?.iataCode} → {selectedDepartureFlight.itineraries?.[0]?.segments?.at(-1)?.arrival?.iataCode}
                        </Text>
                        {tripType === "roundtrip" && selectedReturnFlight && (
                            <Text mb={2}>
                                🔁 Return: {selectedReturnFlight.itineraries?.[0]?.segments?.[0]?.departure?.iataCode} → {selectedReturnFlight.itineraries?.[0]?.segments?.at(-1)?.arrival?.iataCode}
                            </Text>
                        )}
                        <Text fontWeight="bold">
                            Total for {totalPassengers} {totalPassengers > 1 ? "passengers" : "passenger"}: {totalFare} {selectedDepartureFlight.price?.currency}
                        </Text>
                        <Button mt={4} colorScheme="blue" onClick={handleBookNow}>
                            Book Now
                        </Button>
                    </Box>
                ) : null}
            </Flex>
        </Box>
    );
};
